import React, { useCallback, useMemo } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import {
  PanelHeader,
  PanelTitle,
  PanelDetails
} from 'components/attacks/details/AttackStyles'
import Section from 'components/common/Section'
import { type ConcurrentExecutionNodeOffCanvasOutletContext } from 'components/scenarios/concurrent/ConcurrentExecutionNodeOffCanvas'
import { ConcurrentMetaActionsTreeView } from 'components/scenarios/concurrent/ConcurrentMetaActionsTreeView'
import { ConcurrentTreeView } from 'components/scenarios/concurrent/ConcurrentTreeView'
import { GraphEditorContextProvider } from 'components/scenarios/concurrent/GraphEditorContext'
import { ScenarioFailureAlert } from 'components/scenarios/concurrent/ScenarioFailureAlert'
import { ScenarioRollbackFailureAlerts } from 'components/scenarios/concurrent/ScenarioRollbackFailureAlerts'
import { findRunStep } from 'components/scenarios/utils/graph/legacy-graph'
import { NodeSectionType } from 'components/scenarios/utils/graph/messaging'
import { generateGraphNodePathMap } from 'components/scenarios/utils/graph/metadata'
import { useFeatureFlags } from 'hooks/use-feature-flags'
import theme from 'theme'
import {
  type GraphNode,
  type Scenario,
  ScenarioLifecycle
} from 'types/api/scenarios/scenario'

import type { ScenarioPreview } from 'types/api/reliability-tests/preview'
import type { ScenarioRunResponse } from 'types/api/scenarios/runs/scenario-runs'

export const ConcurrentExecution: React.FC<{
  run?: ScenarioRunResponse
  scenario: Pick<Scenario, 'graph'>
  scenarioPreview?: ScenarioPreview
  showTitle?: boolean
  onAttackDetailsClose?(): void
  onAttackDetailsOpen?(node: GraphNode): void
}> = ({
  run,
  scenario,
  scenarioPreview,
  showTitle,
  onAttackDetailsClose,
  onAttackDetailsOpen
}) => {
  const { nodeId: nodeIdFromRouter, step } = useParams()

  const { awsScenarioFiIntegration: isAwsScenarioFiIntegrationEnabled } =
    useFeatureFlags()

  /*
   * The app supports routing from two different path segments:
   * `nodes/:nodeId` | `steps/:step` in order to open the node details drawer.
   */
  const nodeId = nodeIdFromRouter ?? step
  const selectedNode = run && nodeId ? findRunStep(run, nodeId) : undefined

  const { graph } = run ?? scenario
  const graphNodePaths = generateGraphNodePathMap(graph)

  const {
    external_actions: externalActions,
    external_rollbacks: externalRollbacks,
    observers
  } = graph

  // Conditional rendering flags
  const isObserversTreeVisible = observers && observers.length > 0

  const isExternalActionsTreeVisible =
    isAwsScenarioFiIntegrationEnabled &&
    externalActions &&
    externalActions.length > 0

  const isExternalRollbacksTreeVisible =
    isAwsScenarioFiIntegrationEnabled &&
    externalRollbacks &&
    externalRollbacks.length > 0

  const handleCloseDrawer = useCallback(() => {
    if (!selectedNode) return

    onAttackDetailsClose?.()
  }, [selectedNode, onAttackDetailsClose])

  const handleSelectNode = useCallback(
    (node: GraphNode) => {
      onAttackDetailsOpen?.(node)
    },
    [onAttackDetailsOpen]
  )

  const outletContext = useMemo<ConcurrentExecutionNodeOffCanvasOutletContext>(
    () => ({
      run,
      selectedNode,
      onClose: handleCloseDrawer
    }),
    [run, selectedNode, handleCloseDrawer]
  )

  const failedExternalRollbackNodes = useMemo(() => {
    return externalRollbacks?.filter(
      (externalRollback) =>
        externalRollback.state.lifecycle === ScenarioLifecycle.Failed
    )
  }, [externalRollbacks])

  return (
    <Section data-test-scenario-details-attack-configuration>
      {failedExternalRollbackNodes && (
        <ScenarioRollbackFailureAlerts
          externalRollbacks={failedExternalRollbackNodes}
        />
      )}
      <ScenarioFailureAlert run={run} scenario={scenario} />
      {showTitle && (
        <PanelHeader>
          <PanelTitle>Scenario Execution</PanelTitle>
        </PanelHeader>
      )}
      <PanelDetails css={{ marginTop: theme.space[0] }}>
        <GraphEditorContextProvider
          graph={graph}
          graphNodePaths={graphNodePaths}
        >
          {isObserversTreeVisible && (
            <ConcurrentMetaActionsTreeView
              css={{ marginBottom: theme.space[2] }}
              isClickable={Boolean(run)}
              nodes={observers}
              sectionType={NodeSectionType.Observers}
              onClick={handleSelectNode}
            />
          )}
          {isExternalActionsTreeVisible && (
            <ConcurrentMetaActionsTreeView
              isClickable={Boolean(run)}
              nodes={externalActions}
              sectionType={NodeSectionType.ExternalActions}
            />
          )}
          <ConcurrentTreeView
            hasRun={Boolean(run)}
            isClickable={Boolean(run)}
            scenarioPreview={scenarioPreview}
            onClick={handleSelectNode}
          />
          {isExternalRollbacksTreeVisible && (
            <ConcurrentMetaActionsTreeView
              isClickable={Boolean(run)}
              nodes={externalRollbacks}
              sectionType={NodeSectionType.ExternalRollbacks}
            />
          )}
        </GraphEditorContextProvider>
        {/* Node Details Drawer */}
        <Outlet context={outletContext} />
      </PanelDetails>
    </Section>
  )
}
